import { z } from 'zod';
import { environmentIdSchema, isoTimestampSchema } from './common.js';
/**
 * Secrets the platform holds on the operator's behalf. Everything here is
 * encrypted at rest and only ever returned to the browser as metadata: the UI
 * shows whether a credential exists and when it was last updated, never its
 * value.
 */
export const credentialKindSchema = z.enum([
    /** OAuth client credentials for the gateway `/connect/tenant-token` exchange. */
    'gateway-client',
    /** Admin Portal JWT, pasted by a human after logging in with Google. */
    'portal-jwt',
    /** Playwright storageState capturing an authenticated Salesforce session. */
    'salesforce-session',
    /** Gemini API key (or a service account JSON) used to synthesise and hold the call's speech. */
    'google-service-account',
    /**
     * SIP digest credentials for the phone that plays the customer on the far
     * end of the test call. Stored as JSON, see {@link customerSipCredentialSchema}.
     */
    'customer-sip',
    /**
     * Playwright storageState for a Google account signed in to voice.google.com.
     * Lets a Google Voice number play the customer over the public phone network.
     */
    'google-voice-session',
]);
export const CREDENTIAL_LABELS = {
    'gateway-client': 'Gateway OAuth client',
    'portal-jwt': 'Admin Portal token',
    'salesforce-session': 'Salesforce session',
    'google-service-account': 'Google AI key',
    'customer-sip': 'Customer SIP phone',
    'google-voice-session': 'Google Voice session',
};
/**
 * The customer leg of the live call is a WebRTC softphone the runner registers
 * itself. These are the same details any SIP softphone would be configured with.
 */
export const customerSipCredentialSchema = z.object({
    username: z.string().min(1),
    password: z.string().min(1),
    /** SIP domain / realm, for example `node2.wss.registrar.rp4s.ai`. */
    domain: z.string().min(1),
    /** Secure WebSocket the registrar listens on, for example `wss://node2.wss.registrar.rp4s.ai:443`. */
    websocketUrl: z.string().url(),
    /** The phone number (DID) the agent dials to reach this phone. Optional. */
    phoneNumber: z.string().optional(),
    displayName: z.string().optional(),
});
export const credentialMetadataSchema = z.object({
    kind: credentialKindSchema,
    environment: environmentIdSchema.nullable(),
    present: z.boolean(),
    updatedAt: isoTimestampSchema.optional(),
    /** Non-secret hint such as a client id or the account the session belongs to. */
    hint: z.string().optional(),
    /** Known expiry, used to warn before a Salesforce session or JWT lapses. */
    expiresAt: isoTimestampSchema.optional(),
});
export const credentialUpsertSchema = z.object({
    kind: credentialKindSchema,
    /** Null stores the credential once for every environment. */
    environment: environmentIdSchema.nullable().default(null),
    value: z.string().min(1, 'A value is required'),
    hint: z.string().optional(),
});
/** Decrypted bundle handed to a runner container for one run. */
export const runnerSecretsSchema = z.object({
    gatewayClientId: z.string().optional(),
    gatewayClientSecret: z.string().optional(),
    portalJwt: z.string().optional(),
    salesforceStorageState: z.string().optional(),
    googleServiceAccount: z.string().optional(),
    /** JSON matching {@link customerSipCredentialSchema}. */
    customerSip: z.string().optional(),
    /** Playwright storageState JSON for the Google Voice account. */
    googleVoiceStorageState: z.string().optional(),
    /** External Credential principal secrets typed into the run form. */
    externalCredentialClientId: z.string().optional(),
    externalCredentialClientSecret: z.string().optional(),
});
//# sourceMappingURL=credentials.js.map